/* eslint-disable max-classes-per-file --
 * These types are one taxonomy: describeAuthError below switches across all of
 * them, and the subclass relationships (TimeoutError extends NetworkError,
 * OrganizationInactiveError extends ApiError) are the mapping rules. Splitting
 * them across files would scatter a single decision table.
 */
/**
 * A real `Error` subclass, not a plain object literal.
 *
 * Callers render `(mutation.error as Error).message`, which used to be a cast
 * that lied: the thrown value was an object literal with no prototype chain, so
 * `instanceof Error` was false and anything relying on it silently misbehaved.
 *
 * `detail` carries the RFC-7807 `detail` field from b2b-api — the b2b-api error
 * envelope has NO `message` key, so `detail` is where user-facing copy actually
 * lives (personal-email rejection, duplicate-company 409s, rate limiting).
 *
 * Lives here rather than in ./client so that client.ts can depend on this
 * module for its own transport errors without a cycle. Still re-exported from
 * ./client, which is where most callers import it from.
 */
export class ApiError extends Error {
  readonly status?: number;

  readonly detail?: string;

  /**
   * RFC-7807 `type` URI from b2b-api. It is the only field that distinguishes
   * one 4xx from another (e.g. a deactivated org from a plain 403), so it is
   * kept rather than parsed and dropped.
   */
  readonly type?: string;

  /**
   * RFC-7807 `errors[]` from a b2b-api validation 400. Its `detail` is only
   * the generic "The request data is invalid"; the specific reason (e.g. a
   * go-live date outside the window) is in here.
   */
  readonly errors: readonly ApiValidationIssue[];

  constructor(
    message: string,
    status?: number,
    detail?: string,
    type?: string,
    errors: readonly ApiValidationIssue[] = []
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.type = type;
    this.errors = errors;
    // Required for `instanceof ApiError` to hold once TS downlevels to ES5.
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

export interface ApiValidationIssue {
  field: string;
  message: string;
  code?: string;
}

/** Keeps the well-formed entries of an RFC-7807 `errors` field, if any. */
export const parseValidationIssues = (raw: unknown): ApiValidationIssue[] =>
  Array.isArray(raw)
    ? raw
        .filter(
          (e): e is Record<string, unknown> =>
            typeof e === 'object' &&
            e !== null &&
            typeof (e as Record<string, unknown>).message === 'string'
        )
        .map((e) => ({
          field: typeof e.field === 'string' ? e.field : '',
          message: e.message as string,
          ...(typeof e.code === 'string' ? { code: e.code } : {})
        }))
    : [];

/**
 * The most specific server-provided reason for a failure, for showing to the
 * partner: validation messages first, then `detail`. Undefined when the server
 * said nothing usable (transport failure, non-ApiError), so the caller picks
 * its own fallback copy.
 */
export const apiErrorMessage = (error: unknown): string | undefined => {
  if (!(error instanceof ApiError)) return undefined;
  if (error.errors.length > 0) {
    return error.errors.map((e) => e.message).join(' ');
  }
  return error.detail || undefined;
};

/**
 * RFC-7807 `type` slug the API returns when the caller's organization has been
 * deactivated. Kept in sync with `ORGANIZATION_INACTIVE_CODE` in b2b-api.
 */
export const ORGANIZATION_INACTIVE_CODE = 'organization-inactive';

/** Thrown so callers can tell "your access ended" apart from "logged out". */
export class OrganizationInactiveError extends ApiError {
  constructor(message: string, detail?: string) {
    super(message, 403, detail, ORGANIZATION_INACTIVE_CODE);
    this.name = 'OrganizationInactiveError';
    Object.setPrototypeOf(this, OrganizationInactiveError.prototype);
  }
}

/**
 * `fetch` itself rejected, so there is no status to report — DNS, TLS, offline,
 * a CORS preflight rejection, or our own abort timeout. Native fetch signals all
 * of these with a bare `TypeError`, which is why the un-wrapped version of this
 * showed up in PostHog as `error_code: "TypeError"` for ~99% of failures.
 */
export class NetworkError extends Error {
  readonly cause?: unknown;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'NetworkError';
    this.cause = cause;
    Object.setPrototypeOf(this, NetworkError.prototype);
  }
}

/** The request timed out client-side before the server answered. */
export class TimeoutError extends NetworkError {
  constructor(message: string) {
    super(message);
    this.name = 'TimeoutError';
    Object.setPrototypeOf(this, TimeoutError.prototype);
  }
}

/** A 2xx whose body was not the envelope we expect. */
export class BadResponseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BadResponseError';
    Object.setPrototypeOf(this, BadResponseError.prototype);
  }
}

export type AuthErrorCode =
  | 'network_error'
  | 'timeout'
  | 'organization_inactive'
  | 'forbidden'
  | 'not_found'
  | 'rate_limited'
  | 'server_error'
  | 'client_error'
  | 'bad_response'
  | 'unknown';

export interface AuthErrorFacts {
  error_code: AuthErrorCode;
  http_status: number | null;
  problem_type: string | null;
}

// `type` is a full URI in the b2b-api envelope
// (https://example.com/organization-inactive); only the trailing slug is a
// bounded set, so that is all that is safe to send to analytics.
const slug = (type?: string): string | null =>
  type ? (type.split('/').pop() ?? null) : null;

const codeForStatus = (status: number | null): AuthErrorCode => {
  if (status === null) return 'unknown';
  if (status >= 500) return 'server_error';
  if (status === 429) return 'rate_limited';
  if (status === 404) return 'not_found';
  if (status === 403) return 'forbidden';
  if (status >= 400) return 'client_error';
  return 'unknown';
};

/**
 * Reduce a thrown value to a low-cardinality analytics triple.
 *
 * Mapping rule, in order: exception class wins over status, status wins over
 * the RFC-7807 `type`. `type` is reported alongside as `problem_type` and never
 * folded into `error_code`, and `http_status` is always its own property — so a
 * 500 outage and a 404 stay distinguishable, which they were not when the
 * status was interpolated into an error message string.
 *
 * Deliberately omits `error.message`: it is unbounded, and b2b-api's `detail`
 * can echo server prose containing user input.
 */
export const describeAuthError = (error: unknown): AuthErrorFacts => {
  if (error instanceof OrganizationInactiveError) {
    return {
      error_code: 'organization_inactive',
      http_status: 403,
      problem_type: ORGANIZATION_INACTIVE_CODE
    };
  }

  if (error instanceof TimeoutError) {
    return { error_code: 'timeout', http_status: null, problem_type: null };
  }

  if (error instanceof NetworkError) {
    return {
      error_code: 'network_error',
      http_status: null,
      problem_type: null
    };
  }

  if (error instanceof BadResponseError) {
    return { error_code: 'bad_response', http_status: 200, problem_type: null };
  }

  if (error instanceof ApiError) {
    const status = error.status ?? null;
    return {
      error_code: codeForStatus(status),
      http_status: status,
      problem_type: slug(error.type)
    };
  }

  return { error_code: 'unknown', http_status: null, problem_type: null };
};
